/**
 * RestoID — register code verification that signs existing identifiers in itself.
 * ---------------------------------------------------------------------------
 * The mirror of `custom/components/use-sign-in-auto-register`, and a restatement
 * of the upstream container hook
 * `src/containers/VerificationCode/use-register-flow-code-verification.ts` with
 * exactly ONE behavioural change: when the code verifies but the identifier is
 * already taken, upstream stops to ask "Аккаунт … уже существует. Продолжить
 * вход?" and RestoID signs the user in straight away.
 *
 * The reasoning is the sign-in hook's, reflected. This point is reached only
 * after the code sent to that identifier has been entered, so control of it is
 * proven, and the only thing the question can produce is the answer the user
 * already gave by asking to authenticate with that number. RestoID has no
 * separate registration anyway — `custom/pages/Register` redirects /register to
 * /sign-in and accounts are created on first sign-in — so a person who reaches
 * this screen at all got here through `first_screen=identifier:register`, which
 * the `:flow/verification-code` route still serves, and for them "register" and
 * "sign in" were never a choice they meant to make. The residual case is smaller
 * than the sign-in hook's: Logto will not put two accounts on one phone number, so
 * upstream's Отменить led nowhere except back to pick a different number — which a
 * user who wanted a different number can still do from the account they land in.
 *
 * Kept from upstream, deliberately and unchanged:
 *   - the sign-in-disabled guard. If the tenant does not allow SIGN-IN by code
 *     for this identifier type, signing them in is not ours to do; upstream's
 *     alert still fires and still sends the user back;
 *   - `signInWithVerifiedIdentifier` on the existing verification, its error
 *     going to the SignIn (not Register) submit handler, and the `redirectTo`;
 *   - the error-handler map exactly as upstream ordered it, including BOTH
 *     `user.email_already_in_use` and `user.phone_already_in_use` mapping to this
 *     handler, and `preRegisterErrorHandler` — not the sign-in one — spread as
 *     the fallback for the interaction this flow actually started;
 *   - the extra `errorCallback?.()` in `onSubmit`. It is upstream's, it is on top
 *     of the `callback` key already inside `errorHandlers`, and it is reproduced
 *     rather than tidied so this file stays a line-for-line copy of upstream.
 *
 * No terms gate here, also upstream's decision and the right one: this branch
 * signs an EXISTING account in, and `agreeToTermsPolicy` governs registration.
 * The registering direction keeps its gate in `use-sign-in-auto-register`.
 *
 * Changed: the `useConfirmModal` call is gone, and what upstream ran from its
 * `onConfirm` now runs inline and is properly awaited. Its `onCancel` branch
 * (`navigate(-1)`) goes with it, which is why `navigate` is no longer imported.
 * Wired in by `custom/pages/VerificationCode.tsx`.
 */
import { InteractionEvent, type VerificationCodeIdentifier } from '@logto/schemas';
import { useCallback, useMemo } from 'react';

import { identifyWithVerificationCode, signInWithVerifiedIdentifier } from '@/apis/experience';
import useGeneralVerificationCodeErrorHandler from '@/containers/VerificationCode/use-general-verification-code-error-handler';
import useIdentifierErrorAlert, {
  IdentifierErrorType,
} from '@/containers/VerificationCode/use-identifier-error-alert';
import useApi from '@/hooks/use-api';
import type { ErrorHandlers } from '@/hooks/use-error-handler';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import { useSieMethods } from '@/hooks/use-sie';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';

const useRegisterAutoSignIn = (
  identifier: VerificationCodeIdentifier,
  verificationId: string,
  errorCallback?: () => void
) => {
  const redirectTo = useGlobalRedirectTo();

  const { isVerificationCodeEnabledForSignIn } = useSieMethods();

  const handleError = useErrorHandler();

  const signInWithIdentifierAsync = useApi(signInWithVerifiedIdentifier);
  const verifyVerificationCode = useApi(identifyWithVerificationCode);

  const { errorMessage, clearErrorMessage, generalVerificationCodeErrorHandlers } =
    useGeneralVerificationCodeErrorHandler();

  const preRegisterErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.Register, {
    replace: true,
  });

  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn, {
    replace: true,
  });

  const showIdentifierErrorAlert = useIdentifierErrorAlert();

  const identifierExistsErrorHandler = useCallback(async () => {
    const { type, value } = identifier;

    // Upstream's guard, kept verbatim: with sign-in by code switched off for this
    // identifier type we have no business signing them in, so say so and go back.
    if (!isVerificationCodeEnabledForSignIn(type)) {
      void showIdentifierErrorAlert(IdentifierErrorType.IdentifierAlreadyExists, type, value);

      return;
    }

    // Upstream asks the user to confirm continuing as the existing account here.
    // RestoID does not — see the file header. Everything below is upstream's
    // `onConfirm`, unchanged apart from being awaited rather than fired.
    const [error, result] = await signInWithIdentifierAsync(verificationId);

    if (error) {
      await handleError(error, preSignInErrorHandler);

      return;
    }

    if (result?.redirectTo) {
      await redirectTo(result.redirectTo);
    }
  }, [
    identifier,
    isVerificationCodeEnabledForSignIn,
    showIdentifierErrorAlert,
    signInWithIdentifierAsync,
    verificationId,
    handleError,
    preSignInErrorHandler,
    redirectTo,
  ]);

  const errorHandlers = useMemo<ErrorHandlers>(
    () => ({
      'user.email_already_in_use': identifierExistsErrorHandler,
      'user.phone_already_in_use': identifierExistsErrorHandler,
      ...generalVerificationCodeErrorHandlers,
      ...preRegisterErrorHandler,
      callback: errorCallback,
    }),
    [
      identifierExistsErrorHandler,
      generalVerificationCodeErrorHandlers,
      preRegisterErrorHandler,
      errorCallback,
    ]
  );

  const onSubmit = useCallback(
    async (code: string) => {
      const [error, result] = await verifyVerificationCode({
        verificationId,
        identifier,
        code,
      });

      if (error) {
        await handleError(error, errorHandlers);
        errorCallback?.();

        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [
      errorCallback,
      errorHandlers,
      handleError,
      identifier,
      redirectTo,
      verificationId,
      verifyVerificationCode,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useRegisterAutoSignIn;
